import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Heading,
  useToast,
  VStack,
  Spinner,
  Text,
  Divider,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import useSecureApi from '../Authentication/UseSecureApi';

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB
const ALLOWED_FILE_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

const ApplicationForm = () => {
  const { jobId } = useParams();
  const api = useSecureApi();
  const toast = useToast();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    coverLetter: '',
    resume: null,
  });

  const [jobDetails, setJobDetails] = useState(null);
  const [loadingJob, setLoadingJob] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const response = await api.get(`/api/jobs/${jobId}`);
        setJobDetails(response.data);
      } catch (error) {
        toast({
          title: 'Failed to load job details',
          description: error.message || 'Job not found',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoadingJob(false);
      }
    };

    fetchJobDetails();
  }, [api, jobId, toast]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'resume') {
      const file = files[0];
      if (file) {
        if (file.size > MAX_FILE_SIZE) {
          toast({
            title: 'File too large',
            description: 'Resume must be under 3MB.',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
          toast({
            title: 'Invalid file type',
            description: 'Only PDF or Word documents are allowed.',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
          return;
        }
      }
      setFormData((prev) => ({ ...prev, resume: file }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePreview = (e) => {
    e.preventDefault();

    if (!formData.resume) {
      toast({
        title: 'Resume required',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setPreviewing(true);
  };

  const handleSubmit = async () => {
    const applicationJson = JSON.stringify({
      applicantName: formData.fullName,
      email: formData.email,
      phone: formData.phone || null,
      coverLetter: formData.coverLetter,
      status: 'NEW',
      jobId,
    });

    const payload = new FormData();
    payload.append('application', new Blob([applicationJson], { type: 'application/json' }));
    payload.append('resume', formData.resume);

    try {
      setSubmitting(true);
      await api.post('/api/applications', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast({
        title: 'Application submitted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setFormData({ fullName: '', email: '', phone: '', coverLetter: '', resume: null });
      setPreviewing(false);
    } catch (error) {
      toast({
        title: 'Submission failed',
        description: error.message || 'Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingJob) {
    return (
      <Box p={10} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>Loading job details...</Text>
      </Box>
    );
  }

  if (!jobDetails) {
    return (
      <Box p={10} textAlign="center">
        <Text>Job not found.</Text>
      </Box>
    );
  }

  return (
    <Box maxW="xl" mx="auto" mt={10} p={6} borderWidth={1} borderRadius="lg" boxShadow="md">
      <Heading size="lg" mb={2}>Apply for: {jobDetails.title}</Heading>
      <Text mb={6} color="gray.600">
        Job ID: {jobId} | Location: {jobDetails.location}
      </Text>

      {!previewing ? (
        <form onSubmit={handlePreview}>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Full Name</FormLabel>
              <Input
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Jane Doe"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="jane@example.com"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(Optional) +1 234 567 8901"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Cover Letter</FormLabel>
              <Textarea
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleChange}
                placeholder="Write your cover letter..."
                rows={5}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Upload Resume</FormLabel>
              <Input
                type="file"
                name="resume"
                accept=".pdf,.doc,.docx"
                onChange={handleChange}
              />
              {formData.resume && (
                <Text fontSize="sm" color="gray.500">
                  Selected: {formData.resume.name} ({(formData.resume.size / 1024 / 1024).toFixed(2)} MB)
                </Text>
              )}
            </FormControl>

            <Button colorScheme="blue" type="submit" width="full">
              Preview Application
            </Button>
          </VStack>
        </form>
      ) : (
        <>
          <Text fontWeight="bold" fontSize="lg" mb={2}>Please confirm your application:</Text>
          <Divider mb={4} />

          <Text><strong>Full Name:</strong> {formData.fullName}</Text>
          <Text><strong>Email:</strong> {formData.email}</Text>
          <Text><strong>Phone:</strong> {formData.phone || 'N/A'}</Text>
          <Text><strong>Cover Letter:</strong> {formData.coverLetter || 'N/A'}</Text>
          <Text><strong>Resume:</strong> {formData.resume?.name}</Text>

          <VStack mt={6} spacing={4}>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={submitting}
              width="full"
            >
              Confirm & Submit
            </Button>
            <Button variant="outline" onClick={() => setPreviewing(false)} width="full">
              Edit Application
            </Button>
          </VStack>
        </>
      )}
    </Box>
  );
};

export default ApplicationForm;
